import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDesignerScreenContext } from '../../components/Chat/DesignerScreenContext';

import { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AddCircle, Clear, ContentCopy, Delete, Folder, History, Refresh, Search } from '@mui/icons-material';
import { Box, Button, IconButton, InputAdornment, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

import { ContentClient, ContentFolderClient } from '@@api';
import { ArtifactPanel, formatTimestampFull, useApiClient, useCopyToClipboard, useDataGridSearchParams, useIsMobile, useToastService, type ArtifactCategory, type ArtifactFolder } from '@@common';
import { ConfirmDialog, GalleryHeader, GalleryStatistics, getResponsiveDataGridProps } from '@@components';

import { ChangeSetClient, StringTemplateViewModel } from '../../api/api';
import { useChangeSet } from '../../components/ChangeSetContext';
import ContentTemplateVersionsDialog from './ContentTemplateVersionsDialog';
import TemplateChatPanel from './TemplateChatPanel';
import { useTemplateChatAdapter } from './useTemplateChatAdapter';

const pageSizeOptions = [10, 20, 50];
const initialPaginationModel: GridPaginationModel = { pageSize: pageSizeOptions[1], page: 0 };
const initialSortModel: GridSortModel = [{ field: 'name', sort: 'asc' }];
const initialFilters = {
	category: '',
	searchText: '',
	folderId: '',
};

const sortFieldName: Record<string, string> = {
	name: 'Name',
	category: 'Category',
};

export default function ContentTemplateListView() {
	useDesignerScreenContext({ ScreenArea: 'Templates' });
	const navigate = useNavigate();
	const location = useLocation();
	const client = useApiClient(ContentClient);
	const folderClient = useApiClient(ContentFolderClient);
	const changeSetClient = useApiClient(ChangeSetClient);
	const queryClient = useQueryClient();
	const toastService = useToastService();
	const copyToClipboard = useCopyToClipboard();
	const { currentChangeSetId } = useChangeSet();
	const isMobile = useIsMobile();

	const { paginationModel, setPaginationModel, sortModel, setSortModel, filters, setFilter } = useDataGridSearchParams({
		initialPaginationModel,
		initialSortModel,
		initialFilters,
	});
	const [deleteTarget, setDeleteTarget] = useState<StringTemplateViewModel | null>(null);
	const [versionsTemplate, setVersionsTemplate] = useState<StringTemplateViewModel | null>(null);

	const { category, searchText, folderId } = filters;
	const normalizedCategory = category.trim();
	const normalizedSearchText = searchText.trim();
	const templateDetailSearch = location.search;
	const chatContext = useMemo(
		() => ({
			changeSetId: currentChangeSetId,
			category: normalizedCategory || undefined,
		}),
		[currentChangeSetId, normalizedCategory],
	);
	const chatAdapter = useTemplateChatAdapter(undefined, chatContext, false);

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['content', 'templates', paginationModel, sortModel, currentChangeSetId, normalizedCategory, normalizedSearchText, folderId],
		queryFn: () => {
			const sortBy = sortModel.length > 0 ? sortFieldName[sortModel[0].field] : undefined;
			const sortDirection = sortModel.length > 0 ? sortModel[0].sort === 'asc' : undefined;
			return client.search(
				paginationModel.page,
				paginationModel.pageSize,
				sortBy,
				sortDirection,
				currentChangeSetId ?? undefined,
				normalizedCategory || undefined,
				normalizedSearchText || undefined,
				folderId || undefined,
			);
		},
	});
	const { data: folders = [] } = useQuery({ queryKey: ['content', 'folders'], queryFn: () => folderClient.search(undefined) });
	const { data: folderTemplates = [] } = useQuery({
		queryKey: ['content', 'templates', 'folder-items', currentChangeSetId],
		queryFn: async () => {
			const pageSize = 200;
			const templates: StringTemplateViewModel[] = [];
			let pageIndex = 0;
			let hasNextPage: boolean;
			do {
				const page = await client.search(pageIndex, pageSize, 'Name', true, currentChangeSetId ?? undefined, undefined, undefined, undefined);
				templates.push(...page.items);
				hasNextPage = page.hasNextPage;
				pageIndex += 1;
			} while (hasNextPage);
			return templates;
		},
	});
	const folderTemplateCounts = useMemo(() => {
		const counts = new Map<string, number>();
		for (const template of folderTemplates) {
			if (template.folderId) counts.set(template.folderId, (counts.get(template.folderId) ?? 0) + 1);
		}
		return counts;
	}, [folderTemplates]);
	const folderName = folders.find((folder) => folder.id === folderId)?.name;
	const saveFolderMutation = useMutation({
		mutationFn: (edit: { id?: string; name: string; parentId?: string }) => edit.id
			? folderClient.update(edit.id, { name: edit.name.trim(), parentId: edit.parentId })
			: folderClient.create({ name: edit.name.trim(), parentId: edit.parentId }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['content', 'folders'] });
			toastService.success('Folder saved.');
		},
		onError: (error) => toastService.error(error instanceof Error ? error.message : 'Failed to save folder.'),
	});
	const deleteFolderMutation = useMutation({
		mutationFn: (id: string) => folderClient.delete(id),
		onSuccess: async (_result, id) => {
			if (folderId === id) setFilter('folderId', '', true);
			await queryClient.invalidateQueries({ queryKey: ['content', 'folders'] });
			toastService.success('Folder deleted.');
		},
		onError: (error) => toastService.error(error instanceof Error ? error.message : 'Folder cannot be deleted.'),
	});
	const folderCategory: ArtifactCategory = {
		id: 'content-templates',
		label: 'Folders',
		icon: <Folder fontSize="small" />,
		items: [],
		itemCount: folderTemplates.length,
		folders: folders.map((folder): ArtifactFolder => ({
			id: folder.id,
			name: folder.name,
			parentFolderId: folder.parentId,
			order: 0,
			itemCount: folderTemplateCounts.get(folder.id) ?? 0,
		})),
		onCreateFolder: (name, parentId) => saveFolderMutation.mutate({ name, parentId }),
		onRenameFolder: (id, name, parentId) => saveFolderMutation.mutate({ id, name, parentId }),
		onDeleteFolder: (id) => deleteFolderMutation.mutate(id),
	};

	const { data: changeSetsData } = useQuery({
		queryKey: ['changeSets', 'all'],
		queryFn: () => changeSetClient.search(undefined, undefined, 0, 500, undefined, undefined),
	});

	const changeSetNameById = useMemo(() => {
		const map = new Map<string, string>();
		changeSetsData?.items.forEach((cs) => map.set(cs.id, cs.name));
		return map;
	}, [changeSetsData]);

	const deleteMutation = useMutation({
		mutationFn: (id: string) => client.delete(id),
		onSuccess: async () => {
			toastService.success('Template deleted.');
			await queryClient.invalidateQueries({ queryKey: ['content', 'templates'] });
		},
		onError: (error) => toastService.error(error instanceof Error ? error.message : 'Failed to delete template.'),
		onSettled: () => setDeleteTarget(null),
	});

	const rowCount = useMemo(
		() => (data ? paginationModel.page * paginationModel.pageSize + (data.hasNextPage ? paginationModel.pageSize + 1 : data.items.length) : 0),
		[paginationModel, data],
	);
	const templateNameById = useMemo(() => new Map((data?.items ?? []).map((template) => [template.id, template.name])), [data]);
	const handleChatComplete = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: ['content', 'templates'] });
		await queryClient.invalidateQueries({ queryKey: ['content', 'folders'] });
		await refetch();
	}, [queryClient, refetch]);

	const columns: GridColDef<StringTemplateViewModel>[] = [
		{
			field: 'name',
			headerName: 'Name',
			sortable: true,
			flex: 1.4,
			renderCell: (params) => <Link to={{ pathname: `/template/${params.row.id}`, search: templateDetailSearch }}>{params.value}</Link>,
		},
		{ field: 'category', headerName: 'Category', sortable: true, flex: 0.6 },
		{
			field: 'parentTemplateId',
			headerName: 'Master',
			sortable: false,
			flex: 0.9,
			valueGetter: (_value, row) => (row.parentTemplateId ? (templateNameById.get(row.parentTemplateId) ?? row.parentTemplateId) : ''),
		},
		{ field: 'format', headerName: 'Format', sortable: false, flex: 0.5 },
		{ field: 'versionId', headerName: 'Version', sortable: false, width: 90 },
		{
			field: 'changeSetId',
			headerName: 'Change Set',
			sortable: false,
			flex: 0.8,
			valueGetter: (value: string) => changeSetNameById.get(value) ?? value,
		},
		{
			field: 'updatedByName',
			headerName: 'Updated By',
			sortable: false,
			flex: 0.8,
			valueGetter: (_value, row) => row.updatedByName ?? row.updatedById ?? row.createdByName ?? row.createdById ?? '',
		},
		{
			field: 'updatedDate',
			headerName: 'Updated Date',
			sortable: false,
			flex: 0.8,
			valueGetter: (_value, row) => (row.updatedDate ? formatTimestampFull(row.updatedDate) : ''),
		},
		{
			field: 'actions',
			type: 'actions',
			headerName: 'Actions',
			width: 100,
			getActions: (params) => [
				<GridActionsCellItem icon={<History />} label="Versions" onClick={() => setVersionsTemplate(params.row)} />,
				<GridActionsCellItem icon={<ContentCopy />} label="Copy ID" onClick={() => copyToClipboard(params.row.id, String)} showInMenu />,
				<GridActionsCellItem icon={<Delete />} label="Delete" onClick={() => setDeleteTarget(params.row)} showInMenu />,
			],
		},
	];

	const templates = data?.items ?? [];

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader
				title="Templates"
				subtitle="Create and manage reusable content for your experiences."
				utilityActions={
					<Tooltip title="Refresh templates">
						<IconButton aria-label="Refresh templates" onClick={() => refetch()}>
							<Refresh />
						</IconButton>
					</Tooltip>
				}
				primaryAction={<Button variant="contained" startIcon={<AddCircle />} onClick={() => navigate({ pathname: '/template/new', search: templateDetailSearch })}>Create template</Button>}
				statistics={<GalleryStatistics items={[{ label: 'Templates on this page', value: templates.length }, { label: 'Category', value: normalizedCategory || 'All' }, { label: 'Folder', value: folderName || 'All' }]} />}
				search={
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ width: '100%', minWidth: 0 }}>
						<TextField
							size="small"
							placeholder="Search templates…"
							inputProps={{ 'aria-label': 'Search templates' }}
							value={searchText}
							onChange={(event) => setFilter('searchText', event.target.value, true)}
							fullWidth
							sx={{ minWidth: 0, flex: 1 }}
							InputProps={{
								startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
								endAdornment: searchText ? <InputAdornment position="end"><IconButton aria-label="Clear template search" size="small" onClick={() => setFilter('searchText', '', true)}><Clear fontSize="small" /></IconButton></InputAdornment> : undefined,
							}}
						/>
						<ToggleButtonGroup
							size="small"
							value={category}
							exclusive
							onChange={(_, value: string | null) => {
								if (value !== null) setFilter('category', value, true);
							}}
							aria-label="Template category"
							sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'auto' } }}
						>
							<ToggleButton value="">All</ToggleButton>
							<ToggleButton value="Email">Email</ToggleButton>
							<ToggleButton value="SMS">SMS</ToggleButton>
						</ToggleButtonGroup>
					</Stack>
				}
			/>

			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				sx={(theme) => ({
					flex: 1,
					minHeight: 0,
					minWidth: 0,
					overflow: 'hidden',
					gap: `${theme.layout.panelGap}px`,
					px: `${theme.layout.edgeGap}px`,
					pb: `${theme.layout.edgeGap}px`,
				})}
			>
				<Box sx={{ width: { xs: '100%', sm: 'fit-content' }, height: { xs: 220, sm: 'auto' }, minHeight: 0, flexShrink: 0 }}>
					<ArtifactPanel
						title="Folders"
						categories={[folderCategory]}
						staticItems={[{ id: 'all-templates', label: 'All templates', icon: <Folder fontSize="small" /> }]}
						selectedItemId={folderId || 'all-templates'}
						onSelectItem={(itemId) => setFilter('folderId', itemId && itemId !== 'all-templates' ? itemId : '', true)}
						selectFolders
						width={isMobile ? '100%' : undefined}
						defaultWidth={280}
						minWidth={200}
						maxWidth={560}
						storageKey="content-template-folders-panel.width"
						searchPlaceholder="Search folders…"
					/>
				</Box>
				<Stack direction="column" sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
					<Paper sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, minHeight: { xs: 360, sm: 0 }, overflow: 'hidden' }}>
						<DataGrid
							loading={isLoading}
							rows={data?.items}
							rowCount={rowCount}
							columns={columns}
							paginationMode="server"
							sortingMode="server"
							disableColumnFilter
							pageSizeOptions={pageSizeOptions}
							paginationModel={paginationModel}
							onPaginationModelChange={setPaginationModel}
							sortModel={sortModel}
							onSortModelChange={setSortModel}
							columnVisibilityModel={{
								parentTemplateId: !isMobile,
								format: !isMobile,
								versionId: !isMobile,
								changeSetId: !isMobile,
								content: !isMobile,
								updatedByName: !isMobile,
								updatedDate: !isMobile,
							}}
							{...getResponsiveDataGridProps({ isMobile })}
						/>
					</Paper>
				</Stack>
				<TemplateChatPanel adapter={chatAdapter} onComplete={handleChatComplete} />
			</Stack>

			<ConfirmDialog
				open={!!deleteTarget}
				title="Delete Template"
				message={deleteTarget ? `Delete "${deleteTarget.name}"?` : ''}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
				onCancel={() => setDeleteTarget(null)}
			/>
			<ContentTemplateVersionsDialog template={versionsTemplate} changeSetNameById={changeSetNameById} onClose={() => setVersionsTemplate(null)} />
		</Stack>
	);
}
